import {
  ChainNotConfiguredError,
  createEmitter,
  createWalletConnector,
  isSolanaChain,
  type ChainType,
  type ClientType,
  type Connector,
  type ConnectorEventMap,
  type CreateConnectorFn,
  type SolanaChain,
  type WalletConnectorsOption,
} from '@particle-network/connector-core';
import type { Chain } from 'viem/chains';
import { createStore } from 'zustand/vanilla';
import { defaultConnectorFns as defaultSolanaConnectorFns } from './defaultConnectorFns';
import { getConnection } from './getConnection';

type ConnectorsOption = WalletConnectorsOption;

export type WalletParameters = {
  connectorFns?: CreateConnectorFn[];
};

solanaWalletConnectors.type = 'solanaWallet' as const;
export function solanaWalletConnectors(parameters: WalletParameters = {}) {
  const { connectorFns } = parameters;
  const defaultConnectorFns = connectorFns ?? defaultSolanaConnectorFns();

  return createWalletConnector<ConnectorsOption>((options) => {
    const { ...rest } = options;

    const allChains = rest.chains.filter(isSolanaChain) as [Chain, ...Chain[]];
    if (allChains.length == 0) {
      throw new Error('Please add Solana chains in Connectkit options.');
    }
    const chains = createStore(() => allChains);

    function setup(connectorFn: CreateConnectorFn): Connector {
      // Set up emitter and add to connector so they are "linked" together.
      const emitter = createEmitter<ConnectorEventMap>();
      const connector = {
        ...connectorFn({ emitter, chains: chains.getState(), storage: rest.storage }),
        emitter,
        uid: emitter.uid,
        chainType: 'solana' as const,
        walletConnectorType: solanaWalletConnectors.type,
      };

      connector.setup?.();
      return connector;
    }

    const connectors = createStore(() => [...defaultConnectorFns].map(setup));

    function getClient<chainType extends ChainType = SolanaChain>(chainId: number): ClientType<chainType> {
      const chain = chains.getState().find((chain) => chain.id === chainId);
      if (!chain) {
        throw new ChainNotConfiguredError();
      }
      return getConnection(chain) as ClientType<chainType>;
    }

    return {
      get type() {
        return solanaWalletConnectors.type;
      },
      get chains() {
        return chains.getState();
      },
      get connectors() {
        return connectors.getState();
      },
      store: connectors,
      getClient,
      _internal: {
        connectors: {
          setup,
        },
      },
    };
  });
}
